import type React from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'

export interface ShapePointerHandlerOptions {
  shape: ShapeData
  /** A shape locked by another user ignores every pointer interaction. */
  remoteLocked: boolean
  onClick?: (shape: ShapeData, event: React.PointerEvent) => void
  onDoubleClick?: (shape: ShapeData, event: React.MouseEvent) => void
  onContextMenu?: (shape: ShapeData, event: React.MouseEvent) => void
}

export interface ShapePointerHandlers {
  onPointerDown: (event: React.PointerEvent) => void
  onDoubleClick: (event: React.MouseEvent) => void
  onContextMenu: (event: React.MouseEvent) => void
}

/** Pointer handlers for a shape's body path. All of them stop propagation to the canvas. */
export function createShapePointerHandlers({
  shape,
  remoteLocked,
  onClick,
  onDoubleClick,
  onContextMenu,
}: ShapePointerHandlerOptions): ShapePointerHandlers {
  return {
    onPointerDown: (event) => {
      if (remoteLocked || event.button !== 0) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      // preventDefault above suppresses the browser's default focus-blur, so a toolbar button
      // focused by a prior action (e.g. focusSelectTool() after delete) stays focused — which then
      // swallows the next Delete/Backspace keypress via isEditableTarget. Blur explicitly to
      // release it.
      ;(document.activeElement as HTMLElement | null)?.blur()
      onClick?.(shape, event)
    },
    onDoubleClick: (event) => {
      if (remoteLocked) {
        return
      }

      event.stopPropagation()
      onDoubleClick?.(shape, event)
    },
    onContextMenu: (event) => {
      if (remoteLocked) {
        return
      }

      event.stopPropagation()
      onContextMenu?.(shape, event)
    },
  }
}
